// gameInfo.js — plain-language "How to play" copy for each side-game format.
//
// Consumed by StandaloneGamesPanel and PreRoundGamesPanel through a small
// info button ("ⓘ") next to each format option. Copy discipline:
//   * Plain language per CLAUDE.md communication style — no acronyms without
//     the plain-language expansion.
//   * D-278 lexicon: "settle up / net balance / you owe / you're owed" — NEVER
//     "winnings / payout / wager / bet-through-the-app". TournyFlex never
//     handles wager funds; the app displays the running score, players settle
//     any dollars themselves. The lexicon guard in
//     tests/unit/game-info-copy.test.js fails the build if a forbidden phrase
//     appears here.
//   * Two-to-four short paragraphs per format — enough to teach a new player,
//     not so much that the info modal turns into a wall of text.
//
// Shape: { key, label, tagline, paragraphs: string[] }. `label` matches the
// visible name in FORMAT_OPTIONS / FORMAT_CATALOG so consumers can look up
// by key or by label.

const SETTLE_NOTE = 'Money amounts are display-only. TournyFlex never handles wager funds — players settle any net balance themselves.'

export const GAME_INFO = {
  skins: {
    key: 'skins',
    label: 'Skins',
    tagline: 'Win a hole outright and take the "skin" for that hole.',
    paragraphs: [
      'Every hole is worth one skin. Score the lowest number on the hole with no one tied and you win that skin. Ties mean nobody wins the hole.',
      'When a hole ties, the skin carries over to the next hole — the next winner takes both. Carries stack for as many ties in a row as it takes to get an outright winner.',
      'Set a dollar amount per skin at the top. Zero is fine — that turns it into a bragging-rights game and the totals still show live.',
      SETTLE_NOTE,
    ],
  },
  nassau: {
    key: 'nassau',
    label: 'Nassau',
    tagline: 'Three side games in one: front nine, back nine, and overall.',
    paragraphs: [
      'A Nassau is three separate matches played at the same time — front nine, back nine, and the overall 18. Each is scored as best-ball for two sides (Team A vs Team B). Whichever side wins more holes on that segment wins that segment.',
      'You set a dollar unit for each segment (front, back, overall). The totals stay separate so someone can lose the front and still take the back nine.',
      'When there are more than two players, split them into Team A and Team B. Team formats mean each side takes its best score on every hole.',
      SETTLE_NOTE,
    ],
  },
  stableford: {
    key: 'stableford',
    label: 'Stableford',
    tagline: 'Points per hole instead of raw strokes — reward risk-taking.',
    paragraphs: [
      'Every hole earns points based on your score relative to par. Standard scale: bogey +1, par +2, birdie +3, eagle +4, double-eagle +5, and worse-than-bogey scores earn zero. Highest total after 18 wins.',
      'Because bogeys still score and blowups do not sting more than a bogey, you can swing away on a par-5 without wrecking your total. That is the whole point.',
      'Winner-take-all — set a dollar unit at the top and the top total takes the full unit stack. Zero unit is bragging rights only.',
      SETTLE_NOTE,
    ],
  },
  simple: {
    key: 'simple',
    label: 'Simple',
    tagline: 'One dollar per hole per opponent — the easy foursome game.',
    paragraphs: [
      'On every hole, you win one unit from every opponent who scored higher than you and lose one unit to every opponent who scored lower. Tie somebody and nothing changes between the two of you.',
      'The math takes care of itself hole by hole and the running totals show each player as a plus or minus versus the group.',
      'Set the dollar amount per hole per opponent. Small units add up over 18 — a dollar-a-hole game between four players can settle for real money by the last green.',
      SETTLE_NOTE,
    ],
  },
  match_1v1: {
    key: 'match_1v1',
    label: 'Match Play',
    tagline: 'Head-to-head, hole by hole. Win more holes than remain and it is over.',
    paragraphs: [
      'Two sides play each hole against each other — whichever side scores lower wins the hole. Halved holes count for nobody. Sides can be one player each or a team taking its best-ball score.',
      'A match closes out when one side is up by more holes than remain. Win by 3 with 2 left ("3 & 2") and the match is over — subsequent holes are just for fun.',
      'Set a dollar amount for the whole match. Zero is bragging rights. The result is one net balance for the match — no per-hole running dollars.',
      SETTLE_NOTE,
    ],
  },
  wolf: {
    key: 'wolf',
    label: 'Wolf',
    tagline: 'Rotating "Wolf" picks a partner off each tee — or plays alone for double.',
    paragraphs: [
      'Players rotate as the Wolf hole by hole. After the Wolf tees off, the Wolf watches each other player tee off. Right after a shot, the Wolf can pick that player as a partner for the hole (best ball) or wait and see the next tee shot. Wait too long and the last shot in the rotation is the Wolf\'s partner by default.',
      'The Wolf can also go Lone Wolf — play alone against everyone else for double points. Or call Blind Wolf before anyone tees off for triple.',
      'Rotation order defaults to the scorecard order. You can randomize it or drag it into any order you want at setup.',
      'One dollar per point at the top turns it into a running money game; set to zero for bragging rights. Points add up hole by hole and settle at the end.',
      SETTLE_NOTE,
    ],
  },
  vegas: {
    key: 'vegas',
    label: 'Vegas',
    tagline: 'Two two-person teams. Each team\'s two scores become one number — lowest wins.',
    paragraphs: [
      'Play in two teams of exactly two. On every hole each team combines its two scores into a single number — lower digit first (a 4 and a 6 becomes 46). The lower team number wins the hole and the difference is the points swing.',
      'Flip on natural birdie (default on): if the opposing team makes a natural birdie, your team\'s number flips high (46 becomes 64). A natural eagle also doubles the hole.',
      'Blow-up hole: a 10 or higher leads its team\'s number regardless of flip — a 5 and a 10 becomes 105.',
      'Team options: keep the same two-versus-two all round, or switch partners every 6 holes so each pair plays together for a stretch. Ties can wash (pay nobody) or push (the tie carries a multiplier onto the next decided hole). With fixed teams, the team behind can Press a single hole to double just that hole\'s points.',
      SETTLE_NOTE,
    ],
  },
  shuffle_card: {
    key: 'shuffle_card',
    label: 'Shuffle Card',
    tagline: 'Draw a card at each hole and follow whatever rule it prints.',
    paragraphs: [
      'At the start of every hole your group draws a card from a shared deck. Each card prints a rule that changes how the hole is played — new tee, single-club, pick-your-own par, and more. The rule applies to that hole (or in a few cases carries for a set number of holes).',
      'Three modes: Straight Up (one card per hole for the whole group), Double Down (two cards per hole — effects stack), and Duel (two sides, each side draws its own card from a shared deck).',
      'Shuffle Card is a bragging-rights format on its own — the mechanic IS the game. If the group wants money on the round, stack a Skins or Nassau game on top.',
    ],
  },
}

// Ordered format keys, matching the display order in the panels.
export const GAME_INFO_KEYS = [
  'skins', 'nassau', 'stableford', 'simple', 'match_1v1', 'wolf', 'vegas', 'shuffle_card',
]

export function getGameInfo(format) {
  return GAME_INFO[format] || null
}
